import { wordIndexAtTime } from '@src/services/reel/reelSectionWords';
import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import type { ReelSectionWord } from '@src/services/reel/reelSectionWords';

interface SectionTranscriptFoldProps {
  /** The Section's words, timed against the reel. */
  words: ReelSectionWord[];
  /**
   * Reel time to mark — the pointer's while it is on the tracker, else the
   * playhead's. Omitted on a Section the playhead is not in (PLAT-11188 DS11):
   * there is no word being spoken there, and marking one anyway would claim a
   * playback position the row does not have. The window opens at the Section's
   * first line instead.
   */
  time?: number;
  /**
   * Where the pointer sits on the play tracker, while it is on it — a second,
   * lighter marking: those words go yellow where the playhead's stays a
   * highlight *behind* the word. Two treatments of one colour, so scrubbing to
   * read ahead can never be mistaken for having moved the playhead, and the
   * playhead's own mark holds still while it happens.
   */
  previewTime?: number;
  /** Seeks to a clicked word, as an absolute reel time. */
  onSeek?: (time: number) => void;
  testId?: string;
}

/** How many lines of the Section are open at once. */
const VISIBLE_LINES = 4;

/**
 * A click anywhere in the fold — a scrollbar drag included — is about the fold,
 * never about the row it is inside: the row's own click seeks to the Section's
 * start, which is the one thing dragging this scrollbar must not do.
 */
const stopRowSeek = (event: React.MouseEvent) => event.stopPropagation();

/** Guarded for jsdom, which has `matchMedia` only when a test installs it. */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Which of them the marked word's line is held on, counting from the top.
 *
 * The second, not a true middle — there is no middle of an even number of lines,
 * and the choice is not symmetrical anyway: a creator reading this is deciding
 * where to cut *next*, so two lines of what is coming are worth more than a
 * second line of what has already gone by.
 */
const MARKED_LINE_FROM_TOP = 1;

/**
 * The line box every measurement is in units of. Stated rather than measured:
 * the viewport's height has to be a whole number of lines for the clip to fall
 * between lines instead of through one, and reading it back from the DOM to
 * then set it is a layout round trip for a number this file already knows.
 *
 * `--leading-text-sm`'s 20px, since the words moved up a step to `--text-sm`:
 * the passage is what the creator reads in this row, and it now reads at the
 * size the Section names above it do.
 */
const LINE_HEIGHT = 20;

/**
 * PLAT-11188 — the playing Section's transcript, folded open under its row:
 * four lines of it, with the word being spoken marked gold.
 *
 * The inverse of the strip this replaces (PLAT-11073 DS11). There the highlight
 * was pinned and the line slid underneath it, which is precise about "what word
 * is here" and unreadable as a passage. Here the words hold still and the
 * highlight travels, so the creator reads the sentence they are about to cut.
 *
 * Every word of the Section is mounted, with a stable key: a tick or a pointer
 * move changes `data-active` on two spans and touches nothing else in the DOM.
 * That is what the strip could not do — its spans changed identity on every
 * move, so it had to window down to sixteen words.
 *
 * Placement is imperative rather than state (DS3): the transform is a
 * measurement of the DOM this render just produced, and routing it through
 * state would repaint the row twice for every line the playhead crosses.
 */
export const SectionTranscriptFold: React.FC<SectionTranscriptFoldProps> = ({
  words,
  time,
  previewTime,
  onSeek,
  testId,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const runRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLSpanElement>(null);
  const isPlacedRef = useRef(false);
  // Which word the window was last placed for, so a hand on the wheel is not
  // overridden by a render that has nothing new to show.
  const placedForIndexRef = useRef<number | null>(null);

  // -1 marks nothing, which leaves the run untransformed — so a Section opened
  // by a hover reads from its first line, which is where someone asking "what is
  // in this one" wants to start.
  const activeIndex = time === undefined ? -1 : wordIndexAtTime(words, time);
  // The pointer's word, when it is on the tracker. Never the same marking as the
  // playhead's, and never allowed to take it over: on the word they share, the
  // playhead's highlight wins.
  const previewIndex =
    previewTime === undefined ? -1 : wordIndexAtTime(words, previewTime);

  // No dependency list: every render has a word to hold in view, and the
  // measurement is two layout reads.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const run = runRef.current;
    const active = activeRef.current;
    if (!viewport || !run || !active) return;

    // The marked word's line, held one line down the window (DS4): what was
    // just said above it, what is coming below. Clamped at both ends, so the
    // Section's opening and closing lines sit where they actually are rather
    // than against a blank half-window.
    const maxOffset = Math.max(
      0,
      run.offsetHeight - VISIBLE_LINES * LINE_HEIGHT,
    );
    const offset = Math.min(
      maxOffset,
      Math.max(0, active.offsetTop - MARKED_LINE_FROM_TOP * LINE_HEIGHT),
    );

    // Only when the marked word has moved on. Placement used to run on every
    // render, which a clipped window could get away with — nothing else moved
    // it. A real scroller can be moved by hand, and re-placing it on every
    // playback tick would snap a creator reading further down the passage back
    // to the playhead several times a second. Their scroll now stands until the
    // spoken word changes line, which is the one thing that has asked to move
    // the window.
    if (placedForIndexRef.current === activeIndex) return;
    placedForIndexRef.current = activeIndex;

    // The first placement is not a movement — smooth-scrolling the fold open
    // would slide the passage up from its first line every time a row expands.
    // Nor is it one under reduced motion: `scrollTo`'s smooth behaviour is not
    // covered by the media query the way a CSS transition is, so the window
    // arrives rather than travels and it has to be asked here.
    const jumps = !isPlacedRef.current || prefersReducedMotion();
    viewport.scrollTo({ top: offset, behavior: jumps ? 'auto' : 'smooth' });
    isPlacedRef.current = true;
  });

  return (
    <Viewport ref={viewportRef} data-testid={testId} onClick={stopRowSeek}>
      <Run ref={runRef}>
        {words.map((word, index) => {
          const isActive = index === activeIndex;
          const isPreview = !isActive && index === previewIndex;
          return (
            <Word
              key={index}
              ref={isActive ? activeRef : undefined}
              $isActive={isActive}
              $isPreview={isPreview}
              data-active={isActive || undefined}
              data-preview={isPreview || undefined}
              onClick={e => {
                // The row's own click seeks back to the Section's start, which
                // is the one place a word click is not asking for.
                e.stopPropagation();
                onSeek?.(word.startTime);
              }}
            >
              {word.value}
            </Word>
          );
        })}
      </Run>
    </Viewport>
  );
};

/**
 * The lines that are open, and the scroll through the rest of the passage.
 *
 * A real scroller with the Transcript tab's own scrollbar, deliberately: this
 * fold and that tab show the same words, and reading one of them by wheeling and
 * the other by waiting on playback made them two different objects. It replaces
 * a transform-only window (PLAT-11188 DS3), whose reason was that a nested wheel
 * target inside the Section list was worth avoiding (PLAT-11091) —
 * `overscroll-behavior: contain` is what pays for it now: the wheel stops at
 * this box's ends instead of carrying on into the reel behind it.
 *
 * `max-height`, not `height`: a one-line Section folds open one line tall
 * rather than a four-line box with dead space under it — the cap only bites
 * on passages that actually run past it.
 */
const Viewport = styled.div`
  position: relative;
  max-height: ${VISIBLE_LINES * LINE_HEIGHT}px;
  overflow-y: auto;
  overscroll-behavior: contain;

  /* The transcript scroller's own scrollbar, thumb and track alike — "the same
     scrolling" is mostly this. Narrower than that one's 12px: this box is four
     lines tall inside a row, where 12px of thumb would read as furniture. */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.25);
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.4);
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
`;

/**
 * The Section's words, wrapped into lines, scrolled so the marked one is in the
 * window.
 *
 * In normal flow, not absolutely positioned: the run's own wrapped height is
 * what the viewport scrolls through, so a one-line Section folds open one line
 * tall and the four-line cap only bites on longer passages. It is also what
 * makes a word's `offsetTop` a position inside the scrollable content — the
 * number `scrollTop` is set from.
 *
 * A wrapping flex row rather than a paragraph of inline spans, which is also
 * what the strip used: laying the words out as text means the space between two
 * of them belongs to one span or the other, so a line that breaks at that space
 * breaks *inside* a span and its `offsetTop` reports the line above. Flex items
 * wrap at item boundaries, so a word's `offsetTop` is its own line — the number
 * the placement is measured in.
 */
const Run = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  /* No row gap: the lines stack at exactly the line height the viewport counts
     in, so a whole number of them fills it and the next is cleanly clipped. */
  gap: 0 4px;
  font-size: var(--text-sm);
  line-height: ${LINE_HEIGHT}px;
`;

/**
 * The word being spoken, marked the way the Transcript tab marks it: gold
 * *behind* the word with the text going black, not the word's own text going
 * gold. `#dfb615` and black are that view's `.highlighted` rule verbatim
 * (`TranscriptionText`'s `WordLight`) rather than a token — the point is that
 * the same word looks identical on both surfaces, and a near-miss shade reads as
 * a second kind of marking.
 *
 * The rest sit at `--text-secondary`, not the strip's `--text-muted`: here they
 * are the content rather than texture around a highlight, and the gold stands
 * out against them regardless.
 */
const Word = styled.span<{ $isActive: boolean; $isPreview?: boolean }>`
  /* Two markings in one mustard yellow, told apart by how it is applied: the
     playhead's word carries the yellow BEHIND it, the word under the pointer on
     the tracker goes yellow itself. One colour, because both mean "this word" —
     two would read as two unrelated things — and two treatments, so a creator
     running the pointer along the tracker to find a line never has to wonder
     whether the playhead moved. It visibly has not. */
  color: ${props =>
    props.$isActive
      ? 'black'
      : props.$isPreview
        ? '#dfb615'
        : 'var(--text-secondary)'};
  background-color: ${props => (props.$isActive ? '#dfb615' : 'transparent')};
  /* Colour only, never weight: bolding the word under the pointer re-measures
     it, which nudges every word after it along the line as the pointer travels.
     The marking must not move the text it marks. */
  border-radius: 2px;
  cursor: pointer;

  /* Static text makes the words the panel's most precise seek targets, so they
     answer the pointer. Green, and the transcript views' own lightgreen at that
     (their Word / WordLight atoms): a word under the pointer is a word you can
     act on, and it says so the same way on every tab that shows these words.
     Never an underline — a line under a word inside a transcript reads as an
     edit mark. */
  &:hover {
    color: lightgreen;
  }
`;
